import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, CheckCircle2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const topics = [
  { value: "real_estate", label: "Real Estate" },
  { value: "ugc", label: "UGC / Brand Deal" },
  { value: "modeling", label: "Modeling / Acting" },
  { value: "content_creation", label: "Content Creation" },
  { value: "general", label: "General Inquiry" },
];

export const Contact = () => {
  const [form, setForm] = useState({ name: "", email: "", topic: "general", message: "" });
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const onSubmit = async (e) => {
    e.preventDefault();
    if (loading) return;
    if (!form.name || !form.email || !form.message) {
      toast.error("Please fill in all required fields.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/contact`, form);
      setDone(true);
      toast.success("Message sent. I'll reply soon.");
      setForm({ name: "", email: "", topic: "general", message: "" });
    } catch (err) {
      console.error(err);
      toast.error("Couldn't send. Try again or email stef@stefwinstan.com.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="contact" data-testid="contact-section" className="py-24 md:py-32 bg-cream">
      <div className="container-edit grid grid-cols-1 md:grid-cols-12 gap-12 md:gap-16 items-start">
        <div className="md:col-span-5">
          <div className="overline mb-5">— 05 / Contact</div>
          <h2 className="font-serif-heading text-4xl md:text-6xl text-charcoal leading-[1]" data-testid="contact-title">
            Let's make<br />
            <span className="italic text-bronze-deep">something happen.</span>
          </h2>
          <p className="mt-6 text-ink text-base md:text-lg leading-relaxed max-w-md">
            Whether you're buying, selling, pitching a collab, or booking
            talent — drop a note here and I'll personally get back to you.
          </p>
          <div className="mt-10 space-y-4 text-sm">
            <div>
              <div className="overline">Direct</div>
              <a
                href="mailto:stef@stefwinstan.com"
                className="font-serif-heading text-2xl text-charcoal hover:text-bronze transition-colors"
                data-testid="contact-email-link"
              >
                stef@stefwinstan.com
              </a>
            </div>
          </div>
        </div>

        <div className="md:col-span-7">
          {done ? (
            <div className="border border-bronze bg-ivory p-10 md:p-14 text-center" data-testid="contact-success">
              <CheckCircle2 className="mx-auto text-bronze-deep" size={40} strokeWidth={1.2} />
              <h3 className="font-serif-heading text-3xl text-charcoal mt-4">Message sent.</h3>
              <p className="text-ink mt-3">I'll get back to you personally within 24 hours.</p>
              <button
                type="button"
                onClick={() => setDone(false)}
                data-testid="contact-another"
                className="mt-6 text-xs uppercase tracking-[0.22em] text-charcoal border-b border-charcoal hover:text-bronze hover:border-bronze"
              >
                Send another
              </button>
            </div>
          ) : (
            <form onSubmit={onSubmit} className="border border-charcoal/10 bg-ivory p-8 md:p-12" data-testid="contact-form">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-7">
                <div>
                  <label className="overline block mb-2" htmlFor="c-name">Your Name</label>
                  <input id="c-name" className="line-input" value={form.name} onChange={set("name")} data-testid="contact-input-name" required />
                </div>
                <div>
                  <label className="overline block mb-2" htmlFor="c-email">Email</label>
                  <input id="c-email" type="email" className="line-input" value={form.email} onChange={set("email")} data-testid="contact-input-email" required />
                </div>
                <div className="md:col-span-2">
                  <label className="overline block mb-2" htmlFor="c-topic">I'm reaching out about</label>
                  <select id="c-topic" className="line-input" value={form.topic} onChange={set("topic")} data-testid="contact-input-topic">
                    {topics.map((t) => (<option key={t.value} value={t.value}>{t.label}</option>))}
                  </select>
                </div>
                <div className="md:col-span-2">
                  <label className="overline block mb-2" htmlFor="c-msg">Message</label>
                  <textarea id="c-msg" rows={4} className="line-input resize-none" value={form.message} onChange={set("message")} data-testid="contact-input-message" required />
                </div>
              </div>
              <div className="mt-10 flex justify-end">
                <button type="submit" disabled={loading} className="btn-primary disabled:opacity-60" data-testid="contact-submit">
                  {loading ? (<><Loader2 size={14} className="animate-spin mr-2" /> Sending…</>) : "Send Message"}
                </button>
              </div>
            </form>
          )}
        </div>
      </div>
    </section>
  );
};

export default Contact;
